// RuleHelper.ts
import {isEmpty} from 'lodash';
import {FwVersion, IP, Rule, Service} from '../dtos/version.dto';
import {AppWithStatus} from '../dtos/app.dto';
import {DependenciesWarningModalProps} from '../components/features/apps-services/warnings/dependencies-warning-modal';
import {DefaultServicesDto} from '../dtos/system-configuration.dto';
import {ServiceAndStatus} from '../dtos/service-with-status.dto';
import {SystemConfiguration} from './system-configuration';
import {
    ActionType,
    BlockDirectionType,
    FeatureName,
    IPDirection,
    ProtectionPlusExtraOptions,
    ProtectionPlusOptions,
    ServiceActionType
} from './enums';
import {getDefaultRulesByVersionAndTypeOptional, getTypeByValue, isNotSaneAddress} from './versionUtils';

export enum RuleNameAlias {
    ALLOW_TRUST_TO_UNTRUST_NON_WEB_SERVICES = 'TRUST_TO_UNTRUST_NON_WEB_SERVICES_KEY',
    ALLOW_UNTRUST_TO_TRUST_NON_WEB_SERVICES = 'UNTRUST_TO_TRUST_NON_WEB_SERVICES',
    TRUST_TO_UNTRUST_SERVICES = 'TRUST_TO_UNTRUST_SERVICES',
    TRUST_TO_UNTRUST_APPS = 'TRUST_TO_UNTRUST_APPS',
    GEO_IP_DEST = 'GEO_IP_DEST',
    GEO_IP_SRC = 'GEO_IP_SOURCE',
    ALLOW_UNTRUST_DEST = 'ALLOW_UNTRUST_DEST',
    ALLOW_UNTRUST_SRC = 'ALLOW_UNTRUST_SOURCE',
    BLOCK_UNTRUST_DEST = 'DROP_UNTRUST_DEST',
    BLOCK_UNTRUST_SRC = 'DROP_UNTRUST_SOURCE',
    UNTRUST_TO_TRUST_SERVICES = 'UNTRUST_TO_TRUST_SERVICES',
    UNTRUST_TO_TRUST_APPS = 'UNTRUST_TO_TRUST_APPS',
    DROP_ATT_BLOCK_LIST_DEST = 'DROP_ATT_BLOCK_LIST_DEST',
    DROP_ATT_BLOCK_LIST_SRC = 'DROP_ATT_BLOCK_LIST_SRC',
    TRUST_TO_UNTRUST_BLOCKED_SERVICES = 'TRUST_TO_UNTRUST_BLOCKED_SERVICES',
    TRUST_TO_UNTRUST_BLOCKED_APPS = 'TRUST_TO_UNTRUST_BLOCKED_APPS',
    UNTRUST_TO_TRUST_BLOCKED_SERVICES = 'UNTRUST_TO_TRUST_BLOCKED_SERVICES',
    UNTRUST_TO_TRUST_BLOCKED_APPS = 'UNTRUST_TO_TRUST_BLOCKED_APPS',
    DROP_ALL = 'DROP_ALL',
}

export class RuleHelper {

    private static convertAddressToIp(version: FwVersion, agId: string, direction: IPDirection, actionType: ActionType) {
        const addressGroup = version.content.policy.address_groups.find(ag => ag.id === agId);
        if (!addressGroup) return [];

        const addressIds = new Set(addressGroup.addresses.filter(_ => isNotSaneAddress(_)));

        const addresses = version.content.policy.addresses.filter(_ => addressIds.has(_.id));

        return addresses.map((address) => ({
            ...address,
            direction: direction,
            ipType: getTypeByValue(address.value),
            actionType: actionType
        })) as IP[];
    }

    private static isAllowUntrust(version: FwVersion, rule: Rule): boolean {
        return RuleHelper.getRuleAllowUntrustSrc(version).id === rule.id
            || RuleHelper.getRuleAllowUntrustDest(version).id === rule.id;
    }

    private static calcFeatureLabel(profilesWithAction: Record<FeatureName, { alert: number, block: number }>,
        totalRules: number,
        profileName: FeatureName): ProtectionPlusExtraOptions {
        const { block, alert } = profilesWithAction[profileName];
        if ((alert + block) === 0) return 'Inactive';
        if (alert === totalRules) return ProtectionPlusOptions.ALERT;
        if (block === totalRules) return ProtectionPlusOptions.BLOCK;
        return 'Manual';
    }

    static getServiceState(allowRule: Rule, blockRule: Rule, serviceId: string, nonWebAllowRule?: Rule): ServiceActionType {
        const isAllowRuleAllowed = allowRule?.service.services.includes(serviceId);
        const isBlockRuleAllowed = blockRule?.service.services.includes(serviceId);
        const isNonWebRuleAllowed = nonWebAllowRule?.service.services.includes(serviceId) || false;

        if (isBlockRuleAllowed) {
            return ServiceActionType.BLOCK;
        } else if (isAllowRuleAllowed || isNonWebRuleAllowed) {
            return ServiceActionType.ALLOW;
        } else {
            return ServiceActionType.NOT_APPLY;
        }
    }

    static hasAnyAntispywareProfileInactive(selectedFwVersion: FwVersion): boolean {
        const rules = getDefaultRulesByVersionAndTypeOptional(selectedFwVersion, 'allow');
        const filteredRules = rules.filter(rule => !RuleHelper.isAllowUntrust(selectedFwVersion, rule));
        return filteredRules.some(rule =>!rule.profiles[FeatureName.SPYWARE_PROTECTION]);
    }

    static getRuleName(ruleNameAlias: RuleNameAlias) {
        const ruleNames = SystemConfiguration.getInstance().data.ruleNameByAlias;
        return ruleNames[ruleNameAlias];
    }

    static getRuleByAlias(ruleNameAlias: RuleNameAlias, version: FwVersion): Rule {
        const ruleName = RuleHelper.getRuleName(ruleNameAlias);
        const rule = version?.content?.policy?.rules.find(rule => rule.displayName === ruleName);
        if (!rule) {
            throw new Error(`Rule with alias ${ruleNameAlias}, name ${ruleName} not found`);
        }
        return rule;
    }

    static isAllowedByDefault(applicationRule?: Rule): boolean {
        return !applicationRule?.disabled && isEmpty(applicationRule?.application.applications);
    }

    static isAppAllowed(appId: string, allowRule?: Rule, blockRule?: Rule): boolean {
        return RuleHelper.isAllowedByDefault(allowRule) && !blockRule?.application.applications.includes(appId) || !!allowRule?.application.applications.includes(appId);
    }

    static getMyNetworkToInternetAllowedApplications(version: FwVersion): Rule {
        return RuleHelper.getRuleByAlias(RuleNameAlias.TRUST_TO_UNTRUST_APPS, version);
    }

    static getMyNetworkToInternetBlockedApplications(version: FwVersion): Rule {
        return RuleHelper.getRuleByAlias(RuleNameAlias.TRUST_TO_UNTRUST_BLOCKED_APPS, version);
    }

    static getMyNetworkToInternetBlockedServices(version: FwVersion): Rule {
        return RuleHelper.getRuleByAlias(RuleNameAlias.TRUST_TO_UNTRUST_BLOCKED_SERVICES, version);
    }

    static getInternetToMyNetworkAllowedServices(version: FwVersion): Rule {
        return RuleHelper.getRuleByAlias(RuleNameAlias.UNTRUST_TO_TRUST_SERVICES, version);
    }

    static getInternetToMyNetworkAllowedApplications(version: FwVersion): Rule {
        return RuleHelper.getRuleByAlias(RuleNameAlias.UNTRUST_TO_TRUST_APPS, version);
    }

    static getInternetToMyNetworkBlockedApplications(version: FwVersion): Rule {
        return RuleHelper.getRuleByAlias(RuleNameAlias.UNTRUST_TO_TRUST_BLOCKED_APPS, version);
    }

    static getInternetToMyNetworkBlockedServices(version: FwVersion): Rule {
        return RuleHelper.getRuleByAlias(RuleNameAlias.UNTRUST_TO_TRUST_BLOCKED_SERVICES, version);
    }

    static getRuleAllowMyNetworkToInternetNonWebServices(version: FwVersion): Rule {
        return RuleHelper.getRuleByAlias(RuleNameAlias.ALLOW_TRUST_TO_UNTRUST_NON_WEB_SERVICES, version);
    }

    static getRuleAllowInternetToMyNetworkNonWebServices(version: FwVersion): Rule {
        return RuleHelper.getRuleByAlias(RuleNameAlias.ALLOW_UNTRUST_TO_TRUST_NON_WEB_SERVICES, version);
    }

    static getRuleMyNetworkToInternetServices(version: FwVersion): Rule {
        return RuleHelper.getRuleByAlias(RuleNameAlias.TRUST_TO_UNTRUST_SERVICES, version);
    }

    static getRuleGeoIpDest(version: FwVersion): Rule {
        return RuleHelper.getRuleByAlias(RuleNameAlias.GEO_IP_DEST, version);
    }

    static getRuleGeoIpSrc(version: FwVersion): Rule {
        return RuleHelper.getRuleByAlias(RuleNameAlias.GEO_IP_SRC, version);
    }

    static getRuleAllowUntrustDest(version: FwVersion): Rule {
        return RuleHelper.getRuleByAlias(RuleNameAlias.ALLOW_UNTRUST_DEST, version);
    }

    static getRuleAllowUntrustSrc(version: FwVersion): Rule {
        return RuleHelper.getRuleByAlias(RuleNameAlias.ALLOW_UNTRUST_SRC, version);
    }

    static getRuleBlockUntrustDest(version: FwVersion): Rule {
        return RuleHelper.getRuleByAlias(RuleNameAlias.BLOCK_UNTRUST_DEST, version);
    }

    static getRuleBlockUntrustSrc(version: FwVersion): Rule {
        return RuleHelper.getRuleByAlias(RuleNameAlias.BLOCK_UNTRUST_SRC, version);
    }

    static getDropAttBlockListDest(version: FwVersion): Rule {
        return RuleHelper.getRuleByAlias(RuleNameAlias.DROP_ATT_BLOCK_LIST_DEST, version);
    }

    static getDropAttBlockListSrc(version: FwVersion): Rule {
        return RuleHelper.getRuleByAlias(RuleNameAlias.DROP_ATT_BLOCK_LIST_SRC, version);
    }

    static getRuleDropAll(version: FwVersion): Rule {
        return RuleHelper.getRuleByAlias(RuleNameAlias.DROP_ALL, version);
    }

    static getManagedApps(version?: FwVersion,
        allowInboundAppsRule?: Rule,
        allowOutboundAppsRule?: Rule,
        blockInboundAppsRule?: Rule,
        blockOutboundAppsRule?: Rule): AppWithStatus[] {
        return version?.content.managed_apps?.map(app => ({
            id: app.applicationMetadata.id,
            displayName: app.applicationMetadata.displayName,
            description: app.applicationMetadata.description,
            dependencies: app.dependencies,
            inboundAllowed: RuleHelper.isAppAllowed(app.applicationMetadata.id, allowInboundAppsRule, blockInboundAppsRule),
            outboundAllowed: RuleHelper.isAppAllowed(app.applicationMetadata.id, allowOutboundAppsRule, blockOutboundAppsRule)
        })) || [];
    }

    static collectBlockedDependencies(app: AppWithStatus,
        managedApps: AppWithStatus[],
        accessor: 'inboundAllowed' | 'outboundAllowed'): AppWithStatus[] {
        if (!app.dependencies.length) return [];
        return managedApps.filter(appIterator => !appIterator[accessor] &&
            app.dependencies.some(({ id }) => id === appIterator.id));
    }

    static collectAllowedDependentApps(app: AppWithStatus,
        managedApps: AppWithStatus[],
        accessor: 'inboundAllowed' | 'outboundAllowed'): AppWithStatus[] {
        return managedApps.filter(appIterator => appIterator[accessor] &&
            appIterator.dependencies.some(({ id }) => id === app.id));
    }

    static collectAllowedContainerApps(app: AppWithStatus,
                                       managedApps: AppWithStatus[],
                                       accessor: 'inboundAllowed' | 'outboundAllowed'): AppWithStatus[] {
        if (!app.dependencies.length) return [];
        return managedApps.filter(appIterator => appIterator[accessor] &&
            app.dependencies.some(({id}) => id === appIterator.id));
    }

    static allowApp(app: AppWithStatus,
        managedApps: AppWithStatus[],
        updateAppCb: () => void,
        accessor: 'inboundAllowed' | 'outboundAllowed',
        setModalData: (data: DependenciesWarningModalProps) => void) {
        const blockedDependencies = RuleHelper.collectBlockedDependencies(app, managedApps, accessor);
        const nonManagedDependencies = app.dependencies.filter(dep => !managedApps.some(managedApp => managedApp.id === dep.id));
        const affectedAppsNamesSet = new Set([...blockedDependencies.map(dep => dep.displayName),
        ...nonManagedDependencies.map(dep => dep.displayName)]);
        const affectedAppNames = Array.from(affectedAppsNamesSet);
        RuleHelper.handleAppUpdate(app.displayName, affectedAppNames, 'allow', updateAppCb, setModalData);
    }

    static blockApp(app: AppWithStatus,
        managedApps: AppWithStatus[],
        updateAppCb: () => void,
        accessor: 'inboundAllowed' | 'outboundAllowed',
        setModalData: (data: DependenciesWarningModalProps) => void) {
        const allowedDependentApps = RuleHelper.collectAllowedDependentApps(app, managedApps, accessor);
        const allowedContainerApps = RuleHelper.collectAllowedContainerApps(app, managedApps, accessor);
        const affectedAppsNamesSet = new Set([
            ...allowedDependentApps.map(dep => dep.displayName),
            ...allowedContainerApps.map(dep => dep.displayName)
        ]);
        const affectedAppNames = Array.from(affectedAppsNamesSet);
        RuleHelper.handleAppUpdate(app.displayName, affectedAppNames, 'block', updateAppCb, setModalData);
    }

    static handleAppUpdate(appName: string,
        affectedAppNames: string[],
        action: 'allow' | 'block',
        updateAppCb: () => void,
        setModalData: (data: DependenciesWarningModalProps) => void) {
        if (affectedAppNames.length) {
            setModalData({
                open: true,
                appName: appName,
                dependentApps: affectedAppNames,
                okCallback: updateAppCb,
                action: action
            });
        } else {
            updateAppCb();
        }
    }

    static getDefaultServicesWithStatus(defaultServicesDtoList: DefaultServicesDto[], version?: FwVersion): ServiceAndStatus[] {
        if (!version) return [];

        const outboundBlockServicesRule = RuleHelper.getMyNetworkToInternetBlockedServices(version);
        const inboundBlockServicesRule = RuleHelper.getInternetToMyNetworkBlockedServices(version);

        const outboundAllowServicesRule = RuleHelper.getRuleMyNetworkToInternetServices(version);
        const inboundAllowServicesRule = RuleHelper.getInternetToMyNetworkAllowedServices(version);

        const outboundAllowNonWebServicesRule = RuleHelper.getRuleAllowMyNetworkToInternetNonWebServices(version);
        const inboundAllowNonWebServicesRule = RuleHelper.getRuleAllowInternetToMyNetworkNonWebServices(version);

        return defaultServicesDtoList.map(({
            serviceDisplayName,
            sourcePort,
            destinationPort,
            protocol,
            serviceSubDisplayName,
            serviceId,
            serviceDescription,
            serviceTitle,
            highRiskService,
            service,
            webService
        }) => ({
            id: serviceId,
            name: serviceDisplayName,
            subName: serviceSubDisplayName,
            serviceTitle,
            serviceDescription: serviceDescription.replaceAll(String.raw`\n`, '\n').split('\n').filter(desc => !!desc),
            outboundState: RuleHelper.getServiceState(outboundAllowServicesRule, outboundBlockServicesRule, serviceId, outboundAllowNonWebServicesRule),
            inboundState: RuleHelper.getServiceState(inboundAllowServicesRule, inboundBlockServicesRule, serviceId, inboundAllowNonWebServicesRule),
            service, // unnecessary
            highRiskService,
            webService,
            protocol,
            destinationPort,
            sourcePort
        }));
    }

    static getCustomServicesWithStatus(customServicesDtoList: Service[], version?: FwVersion): ServiceAndStatus[] {
        if (!version) return [];

        const outboundAllowServicesRule = RuleHelper.getRuleAllowMyNetworkToInternetNonWebServices(version);
        const inboundAllowServicesRule = RuleHelper.getRuleAllowInternetToMyNetworkNonWebServices(version);

        const outboundBlockServicesRule = RuleHelper.getMyNetworkToInternetBlockedServices(version);
        const inboundBlockServicesRule = RuleHelper.getInternetToMyNetworkBlockedServices(version);

        return customServicesDtoList.map(({
            id,
            displayName,
            description,
            proto,
            sports,
            dports, }) => ({
                id: id,
                name: displayName,
                subName: null,
                serviceTitle: displayName,
                serviceDescription: [description],
                outboundState: RuleHelper.getServiceState(outboundAllowServicesRule, outboundBlockServicesRule, id),
                inboundState: RuleHelper.getServiceState(inboundAllowServicesRule, inboundBlockServicesRule, id),
                highRiskService: false,
                protocol: proto,
                destinationPort: dports.toString(),
                sourcePort: sports.toString(),
                isCustom: true
            }));
    }

    static getAllowList(version?: FwVersion): IP[] {
        if (!version) return [];

        const srcRule = RuleHelper.getRuleAllowUntrustSrc(version);
        const destRule = RuleHelper.getRuleAllowUntrustDest(version);

        const srcRuleAddressGroup = srcRule.src.address.address_groups;
        const destRuleAddressGroup = destRule.dst.address.address_groups;

        const srcIps = srcRuleAddressGroup?.map((_) => RuleHelper.convertAddressToIp(version, _, IPDirection.INTBOUND, ActionType.ALLOW));
        const destIps = destRuleAddressGroup?.map((_) => RuleHelper.convertAddressToIp(version, _, IPDirection.OUTBOUND, ActionType.ALLOW));

        return [...destIps, ...srcIps].flat();
    }

    static getBlockList(version?: FwVersion): IP[] {
        if (!version) return [];

        const untrustDestRule = RuleHelper.getRuleBlockUntrustDest(version);
        const untrustSrcRule = RuleHelper.getRuleBlockUntrustSrc(version);

        const untrustDestRuleAddressGroup = untrustDestRule.dst.address.address_groups;
        const untrustSrcRuleAddressGroup = untrustSrcRule.src.address.address_groups;

        const untrustDestIps = untrustDestRuleAddressGroup?.map((_) => RuleHelper.convertAddressToIp(version, _, IPDirection.OUTBOUND, ActionType.BLOCK));
        const untrustSrcIps = untrustSrcRuleAddressGroup?.map((_) => RuleHelper.convertAddressToIp(version, _, IPDirection.INTBOUND, ActionType.BLOCK));

        return [...untrustDestIps, ...untrustSrcIps].flat();
    }

    static calculateFeaturesState(version: FwVersion): Record<FeatureName, ProtectionPlusExtraOptions> {
        const rules = getDefaultRulesByVersionAndTypeOptional(version, 'allow');
        const filteredRules = rules.filter(rule => !RuleHelper.isAllowUntrust(version, rule));

        const featuresWithAction = Object.values(FeatureName).reduce((acc, featureName) => {
            acc[featureName] = { block: 0, alert: 0 };
            return acc;
        }, {} as Record<FeatureName, { alert: number, block: number }>);

        // check and count the profiles with alert or block action
        // dns is not a part of the profiles
        filteredRules.forEach(rule => Object.values(FeatureName).filter(value => value !== FeatureName.DNS).forEach(featureName => {
            if (!rule.profiles[featureName]) return;

            // count the profiles of antispyware and dns
            if (featureName === FeatureName.SPYWARE_PROTECTION) {
                const matchSpywareAndDnsProfile = version.content.policy.profiles?.find(({ id }) => id === rule.profiles[featureName]);
                if (!matchSpywareAndDnsProfile) return;

                if (matchSpywareAndDnsProfile.antispyware === 'block' || matchSpywareAndDnsProfile.antispyware === 'alert')
                    featuresWithAction[FeatureName.SPYWARE_PROTECTION][matchSpywareAndDnsProfile.antispyware]++;
                if (matchSpywareAndDnsProfile.dns === 'block' || matchSpywareAndDnsProfile.dns === 'alert')
                    featuresWithAction[FeatureName.DNS][matchSpywareAndDnsProfile.dns]++;
            } else {
                const action = rule.profiles[featureName]?.split('_').pop();
                if (action === 'block' || action === 'alert')
                    featuresWithAction[featureName][action]++;
            }
        }));

        return Object.values(FeatureName).reduce((acc, featureName) => {
            acc[featureName] = RuleHelper.calcFeatureLabel(featuresWithAction, filteredRules.length, featureName);
            return acc;
        }, {} as Record<FeatureName, ProtectionPlusExtraOptions>);
    }

    static createCountriesWithStatusMap(selectedFwVersion: FwVersion) {
        const countriesWithStatusMap: Record<string, Record<string, boolean>> = {};

        const dropGeoIpSource = RuleHelper.getRuleGeoIpSrc(selectedFwVersion);
        dropGeoIpSource?.src.address.regions.forEach(region => {
            countriesWithStatusMap[region] = { [BlockDirectionType.IS_INBOUND]: true };
        });

        const dropGeoIpDestination = RuleHelper.getRuleGeoIpDest(selectedFwVersion);
        dropGeoIpDestination?.dst.address.regions.forEach(region => {
            countriesWithStatusMap[region] = {
                ...countriesWithStatusMap[region],
                [BlockDirectionType.IS_OUTBOUND]: true
            };
        });

        return countriesWithStatusMap;
    }
}